import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import Question from './Question';
import { Step, StepContent, Stepper } from '@mui/material';
import StepIconLabel from './StepIconLabel';
import { getSurveyState } from '@act/shared/utilities';
import { surveysRequests } from 'redux/requests/surveys.requests';
import { makeRequest } from 'redux/utils/apiRequest';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { surveyActions } from 'redux/slices/surveys.slice';
import { selectSelectedMember } from 'redux/slices/memberSlice.slice';
import { SurveyTemplateParser } from '@act/storybook';
import {
  applySurveyQuestionFormStateChange,
  type SurveyQuestionFormValue,
} from './surveyQuestionForm.util';

export interface SurveyStepperProps {
  submittingForm: boolean;
  assignedSurvey: SurveyV2.AssignedSurveyDto;
  onFormStateChange?: (isDirty: boolean) => void;
}

type StepsType = ReturnType<
  typeof getSurveyState<
    SurveyV2.QuestionDto,
    SurveyV2.CheckpointDto,
    SurveyV2.QuestionResponseDto
  >
>['steps'];

const SurveyStepper = ({
  assignedSurvey,
  submittingForm,
  onFormStateChange,
}: SurveyStepperProps) => {
  const dispatch = useAppDispatch();

  const selectedMember: UserDto = useAppSelector(selectSelectedMember);

  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [formTracking, setFormTracking] = useState<{
    dirtyQuestions: Set<number>;
    drafts: Map<number, SurveyQuestionFormValue>;
  }>(() => ({
    dirtyQuestions: new Set(),
    drafts: new Map(),
  }));

  const prevStepsRef = useRef<StepsType>();
  const steps = useMemo(() => {
    // If the form is being submitted don't update the steps
    if (submittingForm) {
      return prevStepsRef.current;
    }

    const { steps } = getSurveyState(
      assignedSurvey.questions,
      assignedSurvey.questionResponses,
      assignedSurvey.surveyVersion?.defaultFinalCheckpoint,
      assignedSurvey.phases
    );
    prevStepsRef.current = steps;

    return steps;
  }, [assignedSurvey, submittingForm]);

  async function handleChange(
    answer: SurveyV2.AnswerQuestionDto
  ): Promise<boolean> {
    try {
      const result = await makeRequest(surveysRequests.setQuestionResponse, {
        params: null,
        body: answer,
      });

      dispatch(
        surveyActions.patchAssignedSurveyProgress(result.assignedSurvey)
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  function handleStep(step: number | null): void {
    // Do not clear dirty/draft here — only the active step is mounted, so
    // Previous / step-header navigation must keep in-progress edits for remount
    // and keep the parent unsaved-changes guard accurate until save or discard.
    setSelectedStep(step);
  }

  const handleQuestionFormStateChange = useCallback(
    (
      questionId: number,
      isDirty: boolean,
      draftValue?: SurveyQuestionFormValue
    ) => {
      setFormTracking(prev =>
        applySurveyQuestionFormStateChange(
          prev.dirtyQuestions,
          prev.drafts,
          questionId,
          isDirty,
          draftValue
        )
      );
    },
    []
  );

  const hasAnyDirtyForm = formTracking.dirtyQuestions.size > 0;
  useEffect(() => {
    onFormStateChange?.(hasAnyDirtyForm);
  }, [hasAnyDirtyForm, onFormStateChange]);

  // If activeStep hasn't been set explicitly then we should default to the last step.
  const activeStep = selectedStep === null ? steps.length - 1 : selectedStep;

  return (
    <Stepper nonLinear activeStep={activeStep} orientation="vertical">
      {steps?.map((step, index) => (
        <Step
          key={`${index}-${step.question?.id ?? 'q'}-${step.checkpoint?.id ?? 'c'}`}
        >
          <StepIconLabel
            title={
              step.stopAtCheckpoint
                ? step.checkpoint?.title
                : step.question?.title
            }
            isActive={activeStep === index}
            handleStep={() => handleStep(index)}
          />
          {/*
            Mount question/checkpoint body for the active step only (REEMA-4324).
            Headers stay clickable so guides can jump back; routing still comes
            from getSurveyState + saved responses when an earlier answer changes.
            Dirty drafts live in this parent so remount restores in-progress edits.
          */}
          {activeStep === index && (
            <StepContent style={{ paddingTop: '24px' }}>
              {step.stopAtCheckpoint ? (
                <div>
                  <SurveyTemplateParser
                    template={step.checkpoint.message}
                    member={selectedMember}
                    assignedSurvey={assignedSurvey}
                  />
                </div>
              ) : (
                <QuestionWrapper
                  question={step.question}
                  assignedSurvey={assignedSurvey}
                  member={selectedMember}
                  questionResponse={step.response}
                  draftValue={formTracking.drafts.get(step.question.id)}
                  activeStep={activeStep}
                  numberOfSteps={steps.length}
                  handleChange={handleChange}
                  handleStep={handleStep}
                  onFormStateChange={handleQuestionFormStateChange}
                />
              )}
            </StepContent>
          )}
        </Step>
      ))}
    </Stepper>
  );
};

interface QuestionWrapperProps {
  question: SurveyV2.QuestionDto;
  assignedSurvey: SurveyV2.AssignedSurveyDto;
  member: UserDto;
  questionResponse: SurveyV2.QuestionResponseDto;
  draftValue?: SurveyQuestionFormValue;
  activeStep: number;
  numberOfSteps: number;
  handleChange: (answer: SurveyV2.AnswerQuestionDto) => Promise<boolean>;
  handleStep: (step: number | null) => void;
  onFormStateChange: (
    questionId: number,
    isDirty: boolean,
    draftValue?: SurveyQuestionFormValue
  ) => void;
}

const QuestionWrapper = ({
  question,
  assignedSurvey,
  member,
  questionResponse,
  draftValue,
  activeStep,
  numberOfSteps,
  handleChange,
  handleStep,
  onFormStateChange,
}: QuestionWrapperProps) => {
  const handleQuestionChange = useCallback(
    (isDirty: boolean, nextDraft?: SurveyQuestionFormValue) => {
      onFormStateChange(question.id, isDirty, nextDraft);
    },
    [question.id, onFormStateChange]
  );

  return (
    <Question
      question={question}
      assignedSurvey={assignedSurvey}
      member={member}
      questionResponse={questionResponse}
      draftValue={draftValue}
      activeStep={activeStep}
      numberOfSteps={numberOfSteps}
      handleChange={handleChange}
      handleStep={handleStep}
      onFormStateChange={handleQuestionChange}
    />
  );
};

export default SurveyStepper;
